'use client'

import { useState, useCallback } from 'react'
import { Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MessageActionsProps {
  rawContent: string
  messageId: string
  onCopy?: (messageId: string, content: string) => void
  isCopied?: boolean
  className?: string
}

/**
 * Hover-reveal action toolbar for assistant messages.
 * Currently provides copy-to-clipboard. Future actions:
 * thumbs up/down, regenerate, share.
 */
export function MessageActions({
  rawContent,
  messageId,
  onCopy,
  isCopied: externalCopied,
  className,
}: MessageActionsProps) {
  const [localCopied, setLocalCopied] = useState(false)
  const copied = externalCopied ?? localCopied

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(rawContent)
    setLocalCopied(true)
    onCopy?.(messageId, rawContent)
    setTimeout(() => setLocalCopied(false), 2000)
  }, [rawContent, messageId, onCopy])

  return (
    <div
      className={cn(
        'flex items-center gap-1 pt-1 opacity-0 transition-opacity group-hover:opacity-100',
        className,
      )}
    >
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Copy message to clipboard"
      >
        {copied ? (
          <>
            <Check className="size-3.5 text-green-500" />
            <span className="text-green-500">Copied</span>
          </>
        ) : (
          <>
            <Copy className="size-3.5" />
            <span>Copy</span>
          </>
        )}
      </button>
    </div>
  )
}
